import React, { useCallback } from 'react'
import { Card, IconButton, DataBlock, Row, colors, Paragraph, usePageWidth, Separator } from '@fs/zion-ui'
import { ContentEdit, Person } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  FEMALE,
  getEventLabel,
  getNestedSubelementsByType,
  getPersonName,
  getSexEnumFromResource,
  MALE,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext } from '../../providers/FamilyReviewContext'

import { getPlaceFieldValues, getDateFieldValues, getRelationshipText } from '../../utils/utils'
import { showEssentialInfoHighlights } from '../../utils/highlightUtils'
import LimitedFieldDisplay from './LimitedFieldDisplay'

export const getBackgroundColor = (sex) => {
  if (sex === MALE) return colors.blue02
  if (sex === FEMALE) return colors.red02
  return colors.gray02
}

function EventDisplay({ event }) {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const placeFieldValues = getPlaceFieldValues(event)
  const dateFieldValues = getDateFieldValues(event)
  if (!placeFieldValues || !dateFieldValues) return null
  return (
    <Paragraph size="sm">
      <strong>{getEventLabel(event, zionSlsState.types)}</strong>
      <br />
      {dateFieldValues && dateFieldValues.length > 0 && (
        <>
          <LimitedFieldDisplay
            heading={t('edit-events.fields.date-heading', 'Date')}
            values={dateFieldValues}
            hideLong
          />
          <br />
        </>
      )}

      {placeFieldValues && placeFieldValues.length > 0 && (
        <LimitedFieldDisplay
          heading={t('edit-events.fields.place-heading', 'Place')}
          values={placeFieldValues}
          hideLong
        />
      )}
    </Paragraph>
  )
}

export default function PersonReviewCard({ person, currentTask, editPersonOverlay }) {
  const h = useSlsHighlightsContext()
  const zionSlsState = useSlsStateContext()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })

  const openEditPanel = useCallback(() => {
    dispatch({ type: 'SET_SELECTED_PERSON_ID', value: person.id })
    editPersonOverlay.open()
    showEssentialInfoHighlights({
      h,
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      personIds: [person.id],
      isSmallScreen,
    })
  }, [h, currentTask, dispatch, editPersonOverlay, person.id, isSmallScreen, state.splitScreenPartitionKey])

  const personName = getPersonName({ person, justOneName: true })
  const personSexField = getNestedSubelementsByType(person, 'SEX_CODE')?.[0]
  const sexENUM = getSexEnumFromResource(personSexField)
  const getIconColor = () => {
    if (sexENUM === MALE) return colors.blue10
    if (sexENUM === FEMALE) return colors.red10
    return colors.gray10
  }

  const events = person?.subElements?.filter((el) => el.elementType === 'EVENT')
  const relationshipText = getRelationshipText(person, zionSlsState?.types, currentTask, t)?.toUpperCase()

  return (
    <Card edges="border" backgroundColor={getBackgroundColor(sexENUM)}>
      <Row>
        <Person size="sm" background={getIconColor()} />
        <DataBlock
          growX
          heading={
            <>
              <Paragraph size="xs">{relationshipText}</Paragraph>
              {personName}
            </>
          }
          data={
            <>
              {events &&
                events.map((event) => {
                  return (
                    <span key={event.id}>
                      <Separator size="xs" />
                      {event && <EventDisplay event={event} />}
                    </span>
                  )
                })}
            </>
          }
        />
        <IconButton
          size="sm"
          emphasis="medium"
          Icon={ContentEdit}
          data-testid="Edit Person Icon"
          description={t('family-review.step2.edit-button', 'Edit')}
          linkName="Review Person Edit"
          onClick={openEditPanel}
        />
      </Row>
    </Card>
  )
}
